import React, { useState } from 'react';
import axiosInstance from '../Core/RestHelper';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AzureSetupModalProps {
  open?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface AzureAccountResult {
  tenant_id: string;
  subscription_id: string;
  region: string;
  success: boolean;
  message: string;
  granted_roles: string[];
  missing_permissions: string[];
  remarks: string[];
}

interface AzureVerifyResponse {
  success: boolean;
  message: string;
  accounts: AzureAccountResult[];
  verified_at: string;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const inp: React.CSSProperties = {
  width: '100%', background: '#17171e', border: '1px solid #26262f',
  borderRadius: '6px', padding: '8px 11px', color: '#eeeef5',
  fontSize: '13px', fontFamily: "'Inter',sans-serif", outline: 'none',
  boxSizing: 'border-box', transition: 'border-color .15s',
};

const lbl: React.CSSProperties = {
  fontSize: '12px', fontWeight: 500, color: '#8e8ea8',
  display: 'block', marginBottom: '5px',
};

const btn = (v: 'primary' | 'secondary' | 'ghost' = 'secondary'): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '7px 14px', borderRadius: '6px', fontSize: '13px',
  fontWeight: 500, cursor: 'pointer', border: 'none',
  fontFamily: "'Inter',sans-serif", transition: 'all .15s',
  ...(v === 'primary'  ? { background: '#0078d4', color: '#fff' }
    : v === 'ghost'    ? { background: 'transparent', color: '#8e8ea8', border: '1px solid #26262f' }
    : { background: '#17171e', color: '#eeeef5', border: '1px solid #32323d' }),
});

// ── Small helpers ─────────────────────────────────────────────────────────────

const StepDot: React.FC<{ n: number; active: boolean; done: boolean }> = ({ n, active, done }) => (
  <div style={{
    width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', fontWeight: 700,
    background: done ? '#22c55e' : active ? '#0078d4' : '#1e1e27',
    color: done || active ? '#fff' : '#52525f',
    border: done || active ? 'none' : '2px solid #32323d',
  }}>
    {done
      ? <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="#fff" strokeWidth="2"><path d="M1.5 5.5l3 3 5-5" /></svg>
      : n}
  </div>
);

const InfoBox: React.FC<{ children: React.ReactNode; variant?: 'warn' | 'info' }> = ({ children, variant = 'info' }) => {
  const c = variant === 'warn'
    ? { bg: 'rgba(234,179,8,.06)', border: 'rgba(234,179,8,.2)', text: '#eab308' }
    : { bg: 'rgba(0,120,212,.06)', border: 'rgba(0,120,212,.2)', text: '#60a5fa' };
  return (
    <div style={{ padding: '10px 12px', background: c.bg, border: `1px solid ${c.border}`, borderRadius: '6px', fontSize: '12px', color: c.text, display: 'flex', gap: '8px', lineHeight: 1.6 }}>
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ flexShrink: 0, marginTop: '1px' }}>
        <circle cx="6.5" cy="6.5" r="5.5" /><path d="M6.5 3.5v3M6.5 9v.5" />
      </svg>
      <span>{children}</span>
    </div>
  );
};

const PathHint: React.FC<{ path: string }> = ({ path }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '5px', flexWrap: 'wrap' }}>
    {path.split('→').map((seg, i, arr) => (
      <React.Fragment key={i}>
        <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10.5px', color: '#52525f', background: '#1a1a22', padding: '2px 6px', borderRadius: '4px', border: '1px solid #26262f' }}>
          {seg.trim()}
        </span>
        {i < arr.length - 1 && <span style={{ color: '#32323d', fontSize: '11px' }}>→</span>}
      </React.Fragment>
    ))}
  </div>
);

const NumberedStep: React.FC<{ n: number; title: string; desc: string; hint?: string }> = ({ n, title, desc, hint }) => (
  <div style={{ display: 'flex', gap: '10px' }}>
    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(0,120,212,.15)', color: '#0078d4', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>{n}</div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '3px' }}>{title}</div>
      <div style={{ fontSize: '12px', color: '#8e8ea8', lineHeight: 1.6 }}>{desc}</div>
      {hint && <PathHint path={hint} />}
    </div>
  </div>
);

// ── Verify result panel ───────────────────────────────────────────────────────

const VerifyResultPanel: React.FC<{ response: AzureVerifyResponse }> = ({ response }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    {/* Overall status */}
    <div style={{
      padding: '12px 14px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px',
      background: response.success ? 'rgba(34,197,94,.08)' : 'rgba(244,63,94,.08)',
      border: `1px solid ${response.success ? 'rgba(34,197,94,.25)' : 'rgba(244,63,94,.25)'}`,
    }}>
      {response.success
        ? <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#22c55e" strokeWidth="1.5"><circle cx="9" cy="9" r="7.5" /><path d="M5 9l3.5 3.5 4.5-5" /></svg>
        : <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#f43f5e" strokeWidth="1.5"><circle cx="9" cy="9" r="7.5" /><path d="M9 5.5v4M9 12v.5" /></svg>}
      <div style={{ fontSize: '13px', fontWeight: 600, color: response.success ? '#22c55e' : '#f43f5e' }}>
        {response.message}
      </div>
    </div>

    {/* Per-account breakdown */}
    {response.accounts.map((acc, i) => (
      <div key={i} style={{ border: '1px solid #26262f', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{
          padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px',
          background: acc.success ? 'rgba(34,197,94,.04)' : 'rgba(244,63,94,.04)',
          borderBottom: '1px solid #26262f',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: '#8e8ea8' }}>{acc.subscription_id}</div>
            <div style={{ fontSize: '10.5px', color: '#52525f', marginTop: '2px' }}>Tenant: {acc.tenant_id} · Region: {acc.region}</div>
          </div>
          <span style={{
            fontSize: '10.5px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
            background: acc.success ? 'rgba(34,197,94,.1)' : 'rgba(244,63,94,.1)',
            color: acc.success ? '#22c55e' : '#f43f5e',
          }}>
            {acc.success ? 'Connected' : 'Failed'}
          </span>
        </div>

        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Granted roles */}
          {acc.granted_roles.length > 0 && (
            <div>
              <div style={{ fontSize: '10.5px', fontWeight: 600, color: '#52525f', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '5px' }}>Granted Roles</div>
              {acc.granted_roles.map(role => (
                <div key={role} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '3px 0' }}>
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="#22c55e" strokeWidth="2"><path d="M1.5 5.5l3 3 5-5" /></svg>
                  <span style={{ fontSize: '12px', color: '#8e8ea8' }}>{role}</span>
                </div>
              ))}
            </div>
          )}

          {/* Missing permissions */}
          {acc.missing_permissions.length > 0 && (
            <div>
              <div style={{ fontSize: '10.5px', fontWeight: 600, color: '#52525f', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '5px' }}>Missing Roles</div>
              {acc.missing_permissions.map(role => (
                <div key={role} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '3px 0' }}>
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="#f43f5e" strokeWidth="1.5"><path d="M2 2l7 7M9 2L2 9" /></svg>
                  <span style={{ fontSize: '12px', color: '#f43f5e' }}>{role}</span>
                </div>
              ))}
            </div>
          )}

          {/* Remarks */}
          {acc.remarks.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {acc.remarks.map((r, ri) => (
                <div key={ri} style={{ display: 'flex', gap: '7px', fontSize: '12px', color: '#8e8ea8', padding: '5px 8px', background: '#17171e', borderRadius: '5px', border: '1px solid #26262f' }}>
                  <span style={{ color: acc.success ? '#22c55e' : '#eab308', flexShrink: 0 }}>{acc.success ? '✓' : '⚠'}</span>
                  {r}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    ))}
  </div>
);

// ── Main modal ────────────────────────────────────────────────────────────────

export const AzureSetupModal: React.FC<AzureSetupModalProps> = ({ open = true, onClose, onSuccess }) => {
  const [phase, setPhase] = useState<1 | 2 | 3 | 4>(1);
  const [tenantId, setTenantId] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [region, setRegion] = useState('eastus');
  const [isSaving, setIsSaving] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [verifyResult, setVerifyResult] = useState<AzureVerifyResponse | null>(null);

  if (!open) return null;

  const azureRegions = [
    { value: 'eastus',        label: 'East US' },
    { value: 'eastus2',       label: 'East US 2' },
    { value: 'westus',        label: 'West US' },
    { value: 'westeurope',    label: 'West Europe' },
    { value: 'northeurope',   label: 'North Europe' },
    { value: 'southeastasia', label: 'Southeast Asia' },
    { value: 'uaenorth',      label: 'UAE North' },
    { value: 'australiaeast', label: 'Australia East' },
  ];

  const phases = [
    { n: 1 as const, label: 'Authorize' },
    { n: 2 as const, label: 'Assign Roles' },
    { n: 3 as const, label: 'Subscription' },
    { n: 4 as const, label: 'Verify' },
  ];

  // Phase 1 — redirect to Microsoft consent
  const handleConnect = () => {
    if (!tenantId.trim()) { setError('Tenant ID is required'); return; }
    setError('');
    window.location.href = `/qix-deploy/api/azure/connect-url/${tenantId.trim()}`;
  };

  // Phase 3 — save subscription + region to DB
  const handleSaveSubscription = async () => {
    if (!subscriptionId.trim()) { setError('Subscription ID is required'); return; }
    setError('');
    setIsSaving(true);
    try {
      await axiosInstance.post('/azure/client/', {
        tenant_id: tenantId,
        subscription_id: subscriptionId.trim(),
        region,
      });
      setPhase(4);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save subscription. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Phase 4 — no body, backend reads from DB
  const handleVerify = async () => {
    setError('');
    setVerifyResult(null);
    setIsVerifying(true);
    try {
      const res = await axiosInstance.post('/azure/verify');
      const data: AzureVerifyResponse = res.data;
      setVerifyResult(data);
      if (data.success) onSuccess?.();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const goBack = () => { setError(''); setPhase((phase - 1) as any); };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}
      onClick={onClose}
    >
      <div
        style={{ background: '#111116', border: '1px solid #32323d', borderRadius: '14px', width: '580px', maxWidth: '95vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,.7)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: 'rgba(0,120,212,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🔷</div>
          <div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '16px', fontWeight: 700, color: '#eeeef5' }}>Connect Azure Account</div>
            <div style={{ fontSize: '12px', color: '#52525f' }}>4-step guided setup</div>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#52525f' }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 2l10 10M12 2L2 12" /></svg>
          </button>
        </div>

        {/* Phase stepper */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {phases.map((p, i) => (
            <React.Fragment key={p.n}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <StepDot n={p.n} active={phase === p.n} done={phase > p.n} />
                <span style={{ fontSize: '10px', fontWeight: 500, whiteSpace: 'nowrap', color: phase === p.n ? '#0078d4' : phase > p.n ? '#22c55e' : '#52525f' }}>
                  {p.label}
                </span>
              </div>
              {i < phases.length - 1 && (
                <div style={{ flex: 1, height: '2px', margin: '0 6px', marginBottom: '14px', background: phase > p.n ? '#22c55e' : '#32323d' }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* ── Phase 1: Authorize ── */}
          {phase === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5' }}>Phase 1 — Authorize QixDeploy in Your Tenant</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <NumberedStep
                  n={1}
                  title="Find your Azure Tenant ID"
                  desc="Your Tenant ID identifies your Azure AD organization. You need Global Administrator rights to complete this step."
                  hint="portal.azure.com → Microsoft Entra ID → Overview → Tenant ID"
                />
                <NumberedStep
                  n={2}
                  title="Enter your Tenant ID below and click Connect Azure"
                  desc="You will be redirected to a Microsoft login page. Sign in as a Global Administrator and click Accept. This registers QixDeploy as a service principal in your tenant — no resources are created yet."
                />
                <NumberedStep
                  n={3}
                  title="You will be redirected back here automatically"
                  desc="After approving consent, Microsoft redirects you back. QixDeploy's service principal will then be visible in your tenant under Enterprise Applications."
                  hint="portal.azure.com → Microsoft Entra ID → Enterprise applications → Search: QixDeploy"
                />
              </div>
              <InfoBox variant="warn">
                You must be a <strong>Global Administrator</strong> in your Azure tenant to approve the consent screen. If you are not, ask your IT admin to complete this step.
              </InfoBox>
              <div>
                <label style={lbl}>Azure Tenant ID *</label>
                <input
                  style={{ ...inp, borderColor: error && !tenantId ? '#f43f5e' : '#26262f' }}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={tenantId}
                  onChange={e => setTenantId(e.target.value)}
                  onFocus={e => (e.target.style.borderColor = '#0078d4')}
                  onBlur={e => (e.target.style.borderColor = '#26262f')}
                />
              </div>
              {error && <div style={{ padding: '10px', borderRadius: '6px', background: 'rgba(244,63,94,.1)', border: '1px solid rgba(244,63,94,.25)', color: '#f43f5e', fontSize: '12.5px' }}>{error}</div>}
            </div>
          )}

          {/* ── Phase 2: Assign Roles ── */}
          {phase === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5' }}>Phase 2 — Assign Roles to QixDeploy on Your Subscription</div>
              <InfoBox>
                Complete this after the consent screen is approved. QixDeploy must appear in your Enterprise Applications before roles can be assigned.
              </InfoBox>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <NumberedStep n={1} title="Open your Azure subscription" desc="Navigate to the subscription you want QixDeploy to deploy into." hint="portal.azure.com → Subscriptions → [your subscription name]" />
                <NumberedStep n={2} title='Go to Access control (IAM) → Role assignments tab' desc='Click "Add" → "Add role assignment". You will repeat this twice — once per required role.' hint="Subscription → Access control (IAM) → Role assignments → + Add → Add role assignment" />
                <NumberedStep n={3} title='Assign the "Contributor" role to QixDeploy' desc='Search "Contributor" → select it → Next. Under Members → "User, group, or service principal" → "+ Select members" → search "QixDeploy" → select → Review + assign.' />
                <NumberedStep n={4} title='Assign the "User Access Administrator" role to QixDeploy' desc='Repeat: Add role assignment → search "User Access Administrator" → assign to QixDeploy service principal → Review + assign.' />
                <NumberedStep n={5} title="Confirm both roles appear in the Role assignments list" desc='You should see two rows for QixDeploy — Contributor and User Access Administrator. Both must show scope "This subscription".' hint="Subscription → Access control (IAM) → Role assignments → filter by Name: QixDeploy" />
              </div>
              <div style={{ background: '#17171e', border: '1px solid #26262f', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#52525f', marginBottom: '10px' }}>Required Roles — assign both</div>
                {[
                  { role: 'Contributor', desc: 'Lets QixDeploy create and manage VMs, containers, networks, registries, and key vaults', id: 'b24988ac-6180-42a0-ab88-20f7382dd24c' },
                  { role: 'User Access Administrator', desc: 'Lets QixDeploy assign roles to managed identities it creates during deployments', id: '18d7d88d-d35e-4fb5-a5c3-7773c20a72d9' },
                ].map(r => (
                  <div key={r.role} style={{ display: 'flex', gap: '8px', padding: '8px 0', borderBottom: '1px solid #26262f' }}>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#0078d4" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}><path d="M2 6.5l4 4 5-5" /></svg>
                    <div>
                      <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#eeeef5' }}>{r.role}</div>
                      <div style={{ fontSize: '11.5px', color: '#8e8ea8', marginTop: '2px' }}>{r.desc}</div>
                      <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10px', color: '#52525f', marginTop: '3px' }}>ID: {r.id}</div>
                    </div>
                  </div>
                ))}
              </div>
              <InfoBox variant="warn">
                Scope must be <strong>"This subscription"</strong> — not a resource group. QixDeploy needs subscription-level access to provision resources.
              </InfoBox>
            </div>
          )}

          {/* ── Phase 3: Subscription + Region ── */}
          {phase === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5' }}>Phase 3 — Select Subscription & Region</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <NumberedStep n={1} title="Find your Subscription ID" desc="This is the ID of the subscription you just assigned roles on." hint="portal.azure.com → Subscriptions → [your subscription] → Overview → Subscription ID" />
                <NumberedStep n={2} title="Choose your deployment region" desc="QixDeploy will create all resources (VMs, registries, networks) in this region. Choose the one closest to your users." />
              </div>
              <div>
                <label style={lbl}>Subscription ID *</label>
                <input
                  style={{ ...inp, fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', borderColor: error && !subscriptionId ? '#f43f5e' : '#26262f' }}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={subscriptionId}
                  onChange={e => setSubscriptionId(e.target.value)}
                  onFocus={e => (e.target.style.borderColor = '#0078d4')}
                  onBlur={e => (e.target.style.borderColor = '#26262f')}
                />
              </div>
              <div>
                <label style={lbl}>Deployment Region *</label>
                <select
                  value={region}
                  onChange={e => setRegion(e.target.value)}
                  style={{ ...inp, cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none' stroke='%2352525f' stroke-width='1.5'%3E%3Cpath d='M1 1l4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: '30px' }}
                >
                  {azureRegions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div style={{ background: '#17171e', border: '1px solid #26262f', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#52525f', marginBottom: '8px' }}>QixDeploy will auto-provision in this region</div>
                {[
                  ['qixdeploy-rg',          'Resource group — container for all QixDeploy resources'],
                  ['qixdeploy-{name}-vnet', 'Virtual network — equivalent of AWS VPC'],
                  ['qixdeploy-{name}-vm',   'Virtual machine — equivalent of AWS EC2'],
                  ['qixdeploy{name}acr',    'Container registry — equivalent of AWS ECR'],
                  ['qixdeploy-{name}-kv',   'Key Vault — equivalent of AWS Secrets Manager'],
                ].map(([res, desc]) => (
                  <div key={res} style={{ display: 'flex', gap: '10px', marginBottom: '5px', fontSize: '12px' }}>
                    <span style={{ fontFamily: "'IBM Plex Mono',monospace", color: '#818cf8', minWidth: '170px', flexShrink: 0 }}>{res}</span>
                    <span style={{ color: '#8e8ea8' }}>{desc}</span>
                  </div>
                ))}
              </div>
              {error && <div style={{ padding: '10px', borderRadius: '6px', background: 'rgba(244,63,94,.1)', border: '1px solid rgba(244,63,94,.25)', color: '#f43f5e', fontSize: '12.5px' }}>{error}</div>}
            </div>
          )}

          {/* ── Phase 4: Verify ── */}
          {phase === 4 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5' }}>Phase 4 — Verify Connection</div>
              {!verifyResult ? (
                <>
                  <InfoBox>
                    QixDeploy will now check that both required roles are assigned correctly on your subscription. This takes just a few seconds.
                  </InfoBox>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div style={{ background: 'rgba(34,197,94,.05)', border: '1px solid rgba(34,197,94,.15)', borderRadius: '6px', padding: '10px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#22c55e', marginBottom: '6px' }}>✓ QixDeploy can access</div>
                      {['Resources it creates and manages', 'VMs, registries, networking, secrets', 'Within qixdeploy-rg resource group only'].map(t => (
                        <div key={t} style={{ fontSize: '11.5px', color: '#8e8ea8', marginBottom: '3px' }}>{t}</div>
                      ))}
                    </div>
                    <div style={{ background: 'rgba(244,63,94,.05)', border: '1px solid rgba(244,63,94,.15)', borderRadius: '6px', padding: '10px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#f43f5e', marginBottom: '6px' }}>✕ QixDeploy cannot access</div>
                      {['Your existing Azure resources', 'Other subscriptions', 'Billing information', 'Resources outside QixDeploy RG'].map(t => (
                        <div key={t} style={{ fontSize: '11.5px', color: '#8e8ea8', marginBottom: '3px' }}>{t}</div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <VerifyResultPanel response={verifyResult} />
                  {!verifyResult.success && (
                    <button style={btn('secondary')} onClick={() => { setVerifyResult(null); setPhase(2); }}>
                      ← Fix role assignments and re-verify
                    </button>
                  )}
                </>
              )}
              {error && <div style={{ padding: '10px', borderRadius: '6px', background: 'rgba(244,63,94,.1)', border: '1px solid rgba(244,63,94,.25)', color: '#f43f5e', fontSize: '12.5px' }}>{error}</div>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #26262f', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
          <button style={btn('ghost')} onClick={onClose}>Close</button>
          <div style={{ display: 'flex', gap: '7px' }}>

            {phase > 1 && phase < 4 && (
              <button style={btn('secondary')} onClick={goBack}>← Back</button>
            )}

            {phase === 1 && (
              <>
                <button style={btn('secondary')} onClick={() => {
                  if (!tenantId.trim()) { setError('Enter your Tenant ID first'); return; }
                  setError(''); setPhase(2);
                }}>
                  Already authorized →
                </button>
                <button style={btn('primary')} onClick={handleConnect}>
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6.5 2.5l1.5 1.5-1.5 1.5M10 4H6M3 7v4" /><rect x="1" y="7" width="4" height="4" rx="1" /></svg>
                  Connect Azure →
                </button>
              </>
            )}

            {phase === 2 && (
              <button style={btn('primary')} onClick={() => setPhase(3)}>
                Roles assigned, continue →
              </button>
            )}

            {phase === 3 && (
              <button
                style={{ ...btn('primary'), opacity: isSaving ? 0.7 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}
                onClick={handleSaveSubscription}
                disabled={isSaving}
              >
                {isSaving && <svg style={{ animation: 'spin .8s linear infinite' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" /></svg>}
                {isSaving ? 'Saving…' : 'Save & Continue →'}
              </button>
            )}

            {phase === 4 && !verifyResult && (
              <button
                style={{ ...btn('primary'), opacity: isVerifying ? 0.7 : 1, cursor: isVerifying ? 'not-allowed' : 'pointer' }}
                onClick={handleVerify}
                disabled={isVerifying}
              >
                {isVerifying && <svg style={{ animation: 'spin .8s linear infinite' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" /></svg>}
                {isVerifying ? 'Verifying…' : 'Verify Connection'}
              </button>
            )}

            {phase === 4 && verifyResult && !verifyResult.success && (
              <button style={btn('primary')} onClick={() => { setVerifyResult(null); handleVerify(); }}>
                Re-verify
              </button>
            )}

            {phase === 4 && verifyResult?.success && (
              <button style={btn('primary')} onClick={() => { onSuccess?.(); onClose(); }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1.5 6l3.5 3.5 5.5-6" /></svg>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AzureSetupModal;